<template>
  <div class="puredata-graph">
    <figure ref="figure">
      <canvas :id=chartId ref="canvas" />
    </figure>
    <div class="legend" v-if="show_legend">
      <table>
        <thead><tr>
          <th></th>
          <th>{{ 'feelgrid:puredata.legend.device' | trans }}</th>
          <th>{{ 'feelgrid:puredata.legend.average' | trans }}</th>
          <th v-if="interval === 'point'">{{ 'feelgrid:puredata.legend.latest' | trans }}</th>
        </tr></thead>
        <tbody>
          <tr
            v-for="(device, device_id) in graph.devices"
            @mouseenter="set_hl(device_id)"
            @click="fix_hl(device_id)"
            @mouseleave="reset_hl(device_id)"
            :class="{highlight: highlight === device_id}"
            :style="{backgroundColor: 'rgba(' + color[device_id] + ', 0.5)'}"
          >
            <td><i :style="{backgroundColor: 'rgba(' + color[device_id] + ', 0.5)'}"></i></td>
            <td>{{ device.name | text }}</td>
            <td class="val">{{ average[device_id] }}</td>
            <td class="val" v-if="interval === 'point'">{{ latest[device_id] }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
  import {Scatter, mixins} from 'vue-chartjs'
  import {dateFromApi} from '@/providers/fields'
  import {sensor_value} from '../helpers'
  import css_vars from '../scss/_variables.scss';
  import Chart from 'chart.js';
  import _ from 'lodash';

  //Delete width and height props
  var CustomScatter = Object.assign({}, Scatter);
  CustomScatter.props = Object.assign({}, Scatter.props);
  delete CustomScatter.props.width;
  delete CustomScatter.props.height;

  export default {
    extends: CustomScatter,
    mixins: [mixins.reactiveData],
    props: {
      graph: {required: true},
      interval: {default: 'point'},
      show_legend: {default: true},
      big_dots: {default: false},
    },
    data () {return {
      colors: _.chunk(css_vars.rgbs.split(',').map(r => r.trim()), 3),
      highlight: false,
      fixed_highlight: false,
    }},
    computed: {
      color () {
        var c = {};
        for (var device_id in this.graph.devices) {
          let i = Object.keys(this.graph.devices).indexOf(device_id) % this.colors.length;
          c[device_id] = this.colors[i].join(',');
        }
        return c;
      },
      average () {
        var c = {};
        for (var device_id in this.graph.devices) {
          if (this.graph.loading.includes(device_id)) {
            c[device_id] = '';
            continue;
          }
          if (!this.graph.datasets[device_id] || !this.graph.datasets[device_id].length) {
            c[device_id] = '';
            continue;
          }
          let vals = this.graph.datasets[device_id].map(o => o.y || o.avg || 0);
          c[device_id] = this.format_val(_.sum(vals) / vals.length);
        }
        return c;
      },
      latest () {
        var c = {};
        for (var device_id in this.graph.devices) {
          if (this.graph.loading.includes(device_id)) {
            c[device_id] = '';
            continue;
          }
          if (!this.graph.datasets[device_id] || !this.graph.datasets[device_id].length) {
            c[device_id] = '';
            continue;
          }
          let len = this.graph.datasets[device_id].length;
          c[device_id] = this.format_val(this.graph.datasets[device_id][len-1].y);
        }
        return c;
      },
      unit () {
        return this.graph.type.measurement_unit;
      },
      data () {
        var datasets = [];
        var weak = this.interval === 'point' || this.highlight ? '0.05' : '0.3';
        for (var device_id in this.graph.devices) {
          if (typeof this.graph.datasets[device_id] !== 'object') {
            continue;
          }
          let color = this.color[device_id];
          let highlighted = (this.highlight && this.highlight === device_id);
          let faded = (this.highlight && !highlighted);
          let strong_color = 'rgba(' + color + ',' + (highlighted ? '1' : (faded ? '0.2' : '0.5')) + ')';
          let weak_color = 'rgba(' + color + ',' + (highlighted ? '0.5' : weak) + ')';
          let styling = {
            label: this.graph.devices[device_id].name,
            borderColor: strong_color,
            backgroundColor: weak_color,
            pointBackgroundColor: weak_color,
            pointBorderColor: faded ? strong_color : 'rgba(' + color + ')',
            borderWidth: (this.interval === 'point' ? 1 : 2),
            pointRadius: this.big_dots ? 4 : 1,
            pointHoverRadius: 10,
            fill: true,
            tension: (this.interval === 'point' ? 0 : 0.4),
            showLine: true
          };
          if (this.graph.datasets[device_id].length) {
            let first = this.graph.datasets[device_id][0];
            if (first.hasOwnProperty('y')) {
              datasets.push(Object.assign({
                data: this.graph.datasets[device_id],
              }, styling));
            } else if (first.hasOwnProperty('avg')) {
              //add lines for min, average and max
              datasets.push(Object.assign({
                data: this.graph.datasets[device_id].map(o => ({t:o.t, y:o.min})),
              }, styling, {
                fill: "+2",
                borderColor: weak_color,
                borderWidth: 1,
              }));

              datasets.push(Object.assign({
                data: this.graph.datasets[device_id].map(o => ({t:o.t, y:o.avg})),
              }, styling, {
                fill: false,
              }));
              
              datasets.push(Object.assign({
                data: this.graph.datasets[device_id].map(o => ({t:o.t, y:o.max})),
              }, styling, {
                fill: false,
                borderColor: weak_color,
                borderWidth: 1,
              }));
            }
          } else {
            datasets.push(Object.assign({
              data: [],
            }, styling));
          }
        }
        return {datasets};
      },
      options () {
        var unit = '';
        var tooltipFormat = 'DD.MM. HH:mm:ss';
        if (this.interval === 'days') {
          tooltipFormat = 'DD.MM.';
          unit = 'day';
        } else if (this.interval === 'hours') {
          tooltipFormat = 'DD.MM. HH:mm';
          unit = 'hour';
        }
        var defaultopts = {
          legend: {
            display: false,
          },
          animation: {
            duration: 0,
          },
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            yAxes: [{
              gridLines: {
                lineWidth: 1,
                drawBorder: true,
              },
              ticks: {
                // Include the unit in ticks
                callback: this.format_val,
              },
            }],
            xAxes: [{
              type: 'time',
              time: {
                displayFormats: {
                  day: 'DD.MM.',
                  hour: 'DD.MM. HH:mm',
                  minute: 'HH:mm',
                  second: 'HH:mm:ss',
                  millisecond: 'HH:mm:ss.SSS',
                },
                unit,
                tooltipFormat,
              },
              gridLines: {
                lineWidth: 1,
                drawBorder: true,
              },
            }],
          },
          hover: {
            animationDuration: 0,
          },
          tooltips: {
            custom: function(tooltip) {
              if (!tooltip) return;
              // disable displaying the color box;
              tooltip.displayColors = false;
            },
            callbacks: {
              title: (items, data) => {
                if (!items.length) {
                  return;
                }
                if (this.interval === 'point') {
                  return items[0].xLabel;
                } else {
                  var i = items[0].datasetIndex;
                  return [
                    data.datasets[i].label,
                    items[0].xLabel,
                  ];
                }
              },
              label: (item, data) => {
                var i = item.datasetIndex;
                if (this.interval === 'point') {
                  return data.datasets[i].label + ': ' + this.format_val(item.yLabel);
                } else {
                  var min_i = Math.floor(i / 3) * 3;
                  var p = [];
                  var stretch = ['min', 'avg', 'max'];
                  for (let j in stretch) {
                    let k = min_i + Number(j);
                    if (data.datasets[k] && data.datasets[k].data[item.index]) {
                      p.unshift(
                        this.$t('feelgrid:puredata.value.' + stretch[j])
                        + ': ' +
                        this.format_val(data.datasets[k].data[item.index].y),
                      );
                    }
                  }
                  return p;
                }
              },
            }
          },
        };
        if (typeof this.opts === 'object') {
          _.merge(defaultopts, this.opts);
        }
        return defaultopts;
      },
    },
    methods: {
      fix_hl (device_id) {
        if (this.fixed_highlight && this.highlight === device_id) {
          this.fixed_highlight = false;
        } else {
          this.highlight = device_id;
          this.fixed_highlight = true;
        }
      },
      set_hl (device_id) {
        if (this.highlight != device_id && !this.fixed_highlight) {
          this.highlight = device_id;
        }
      },
      reset_hl (device_id) {
        if (this.highlight === device_id && !this.fixed_highlight) {
          this.highlight = false;
        }
      },
      format_val (v) {
        return sensor_value(v, this.graph.type);
      }
    },
    watch: {
      data: {
        deep: true,
        handler (v) {
          this.chartData = v;
        },
      },
    },
    mounted () {
      this.$nextTick(() => {
        this.chartData = this.data;
      });
      this.renderChart(this.chartData, this.options);
      if (Object.keys(this.graph.devices).length === 1 && !this.show_legend) {
        this.set_hl(Object.keys(this.graph.devices)[0]);
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import '../scss/variables';

  .puredata-graph {
    display: flex;
    .legend {
      flex-basis: 100%;
      width: 100%;
      flex-shrink: 0;
      @media (min-width: $sm-min) {
        flex-basis: 320px;
        width: 320px;
      }
      @media (min-width: $md-min) {
        padding-left: 15px;
      }
      overflow-x: auto;
    }
    figure {
      flex-grow: 1;
    }
    table {
      width: 100%;
      border-spacing: 0;
      td, th {
        @include font-size(16px);
        text-align: right;
        padding: 2px 0 2px 5px;
        &:first-child {
          vertical-align: middle;
          padding: 0 0 0 2px;
          width: 18px;
        }
      }
      th {
        padding-left: 0;
        color: #8A8A8A;
        font-weight: 700;
      }
      td {
        font-weight: 400;
        i {
          display: inline-block;
          vertical-align: middle;
          width: 16px;
          height: 16px;
        }
        &.val {
          white-space: nowrap;
        }
      }
      tr:not(.highlight) {
        background-color: transparent !important;
      }
      tr.highlight {
        background-image: linear-gradient(to right, transparent, white);
      }
    }
    @media (max-width: $sm-max) {
      display: block;
    }
  }
</style>


// WEBPACK FOOTER //
// src/apps/feelgrid/assets/Graph.vue